import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { FilterOptions } from '../../types';

interface FilterPanelProps {
  onFilter: (filters: FilterOptions) => void;
  loading: boolean;
}

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const YEARS = Array.from({ length: 11 }, (_, i) => 2023 - i);

const COMMON_CPT_CODES = [
  { code: '80048', description: 'Basic Panel' },
  { code: '80050', description: 'Health Panel' },
  { code: '80053', description: 'Metabolic Panel' },
  { code: '81001', description: 'Urine Test' },
  { code: '81002', description: 'Urine Strip' },
  { code: '81003', description: 'Urine Auto' },
  { code: '81025', description: 'Pregnancy Urine' },
  { code: '82043', description: 'Microalbumin' },
  { code: '82248', description: 'Direct Bilirubin' },
  { code: '82306', description: 'VitD' },
  { code: '82565', description: 'Creatinine' },
  { code: '82570', description: 'Urine Creatinine' },
  { code: '83036', description: 'A1C' },
  { code: '83540', description: 'Iron' },
  { code: '83550', description: 'Iron Binding' },
  { code: '83615', description: 'LDH' },
  { code: '83690', description: 'Lipase' },
  { code: '83721', description: 'LDL' },
  { code: '83735', description: 'Magnesium' },
  { code: '83880', description: 'BNP' },
  { code: '83970', description: 'PTH' },
  { code: '84100', description: 'Phosphorus' },
  { code: '84132', description: 'Potassium' },
  { code: '84145', description: 'Procalcitonin' },
  { code: '84439', description: 'Free T4' },
  { code: '84443', description: 'TSH' },
  { code: '84450', description: 'ALT' },
  { code: '84460', description: 'AST' },
  { code: '84484', description: 'Troponin' },
  { code: '84520', description: 'Urea Nitrogen' },
  { code: '85014', description: 'Hemoglobin' },
  { code: '85018', description: 'Hemoglobin' },
  { code: '85025', description: 'CBC Diff' },
  { code: '85027', description: 'CBC' },
  { code: '85032', description: 'Cell Count' },
  { code: '85610', description: 'PT INR' },
  { code: '85730', description: 'aPTT' },
  { code: '86308', description: 'Monospot' },
  { code: '86318', description: 'H. Pylori' },
  { code: '87040', description: 'Blood Culture' },
  { code: '87070', description: 'Wound Culture' },
  { code: '87086', description: 'Urine Culture' },
  { code: '87184', description: 'Sensitivity' },
  { code: '87186', description: 'Antibiotic Panel' },
  { code: '87340', description: 'HepB Surface' },
  { code: '87426', description: 'COVID Antigen' },
  { code: '87428', description: 'COVID+Flu Combo' },
  { code: '87484', description: 'Respiratory DNA' },
  { code: '87502', description: 'Flu PCR' },
  { code: '87503', description: 'Flu PCR' },
  { code: '87581', description: 'Viral PCR' },
  { code: '87635', description: 'COVID PCR' },
  { code: '87640', description: 'Strep A DNA' },
  { code: '87641', description: 'Strep B DNA' },
  { code: '87651', description: 'Respiratory DNA' },
  { code: '87652', description: 'Respiratory DNA' },
  { code: '87804', description: 'Flu Rapid' },
  { code: '87807', description: 'Viral Rapid' },
  { code: '87880', description: 'Strep Rapid' },

  { code: '70030', description: 'Skull XR' },
  { code: '70210', description: 'Sinus XR' },
  { code: '71045', description: 'Chest XR1' },
  { code: '71046', description: 'Chest XR2' },
  { code: '73030', description: 'Shoulder XR' },
  { code: '73060', description: 'Humerus XR' },
  { code: '73110', description: 'Wrist XR' },
  { code: '73130', description: 'Hand XR' },
  { code: '73502', description: 'Hip XR' },
  { code: '73600', description: 'Foot XR' },
  { code: '73630', description: 'Ankle XR' },
  { code: '74018', description: 'Abdomen XR' },
  { code: '74176', description: 'CT Abdomen' },
  { code: '74177', description: 'CT Abdomen+' },
  { code: '74178', description: 'CT Abd/Pelvis' },
  { code: '76700', description: 'Abdomen US' },
  { code: '76705', description: 'RUQ US' },
  { code: '76770', description: 'Kidney US' },
  { code: '76801', description: 'OB US' },
  { code: '76802', description: 'OB US Add' },
  { code: '76830', description: 'Pelvic US' },
  { code: '76856', description: 'Pelvic US' },
  { code: '76857', description: 'Limited US' },
  { code: '77012', description: 'CT Guidance' },
  { code: '77067', description: 'Mammogram' },
  { code: '77080', description: 'Bone Density' },
  { code: '77085', description: 'Bone Scan' },

  { code: '99202', description: 'Office Low' },
  { code: '99203', description: 'Office Low+' },
  { code: '99204', description: 'Office Mod' },
  { code: '99205', description: 'Office High' },
  { code: '99211', description: 'Nurse Visit' },
  { code: '99212', description: 'Office Simple' },
  { code: '99213', description: 'Office Moderate' },
  { code: '99214', description: 'Office Extended' },
  { code: '99215', description: 'Office Complex' },

  { code: '99231', description: 'Hospital Low' },
  { code: '99232', description: 'Hospital Mod' },
  { code: '99233', description: 'Hospital High' },

  { code: '11721', description: 'Nail Debride' },
  { code: '17000', description: 'Skin Destroy' },
  { code: '17003', description: 'Skin Destroy+' },
  { code: '17004', description: 'Skin Destroy Ext' },
  { code: '17110', description: 'Lesion Remove' },
  { code: '20610', description: 'Joint Inject' },
  { code: '20611', description: 'Joint Inject+' },
  { code: '36415', description: 'Blood Draw' },
  { code: '36416', description: 'Finger Stick' },
  { code: '36591', description: 'Dialysis Draw' },
  { code: '36592', description: 'Dialysis Test' },
  { code: '40490', description: 'Tissue Sample' },
  { code: '45378', description: 'Colonoscopy' },
  { code: '45380', description: 'Colon Biopsy' },
  { code: '45385', description: 'Colon Polyp' },
  { code: '69209', description: 'Ear Wax' },
  { code: '69210', description: 'Ear Wax+' },
  { code: '93000', description: 'EKG' },
  { code: '93005', description: 'EKG Tracing' },
  { code: '93010', description: 'EKG Read' },
  { code: '93017', description: 'Stress Test' },
  { code: '93224', description: 'Holter' },
  { code: '93225', description: 'Holter Start' },
  { code: '93226', description: 'Holter Read' },
  { code: '94760', description: 'Oxygen Check' },

  { code: 'A0425', description: 'Ambulance Miles' },
  { code: 'A0427', description: 'ALS Ambulance' },
  { code: 'A0428', description: 'BLS Ambulance' },
  { code: 'A0429', description: 'BLS Ambulance+' },
  { code: 'A0998', description: 'Ambulance Treat' },
  { code: 'G0008', description: 'Flu Shot' },
  { code: 'G0009', description: 'Pneumo Shot' },
  { code: 'G0444', description: 'Depression Screen' },
  { code: 'G0447', description: 'Obesity Counsel' },
  { code: 'G0471', description: 'Lab Collection' },
  { code: 'G2023', description: 'COVID Sample' },
  { code: 'G2024', description: 'COVID Sample+' },
  { code: 'J0696', description: 'Ceftriaxone' },
  { code: 'J1100', description: 'Dexamethasone' },
  { code: 'J1885', description: 'Ketorolac' },
  { code: 'J3420', description: 'B12' },
  { code: 'Q0091', description: 'Pap Collect' },
  { code: 'Q3014', description: 'Telehealth Fee' },
  { code: 'Q9967', description: 'CT Contrast' }
];

export const FilterPanel: React.FC<FilterPanelProps> = ({ onFilter, loading }) => {
  const [filters, setFilters] = useState<FilterOptions>({
    year: 2023,
    state: '',
    city: '',
    zip: '',
    npi: '',
    cptCode: '',
    limit: 50,
  });

  const handleFilterChange = (field: keyof FilterOptions, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilter(filters);
  };


  const handleReset = () => {
    const resetFilters = {
      year: 2023,
      state: '',
      city: '',
      zip: '',
      npi: '',
      cptCode: '',
      limit: 50,
    };
    setFilters(resetFilters);
    // Submit the reset filters to go back to page 1
    onFilter(resetFilters);
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit}>
          {/* Provider Filters - Horizontal Layout */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Provider Filters</h3>
            </div>
            
            {/* First Row - Main Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
              {/* Year */}
              <div>
                <label className="block text-sm font-medium mb-1">Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                >
                  {YEARS.map(year => (
                    <option key={year} value={year.toString()}>{year}</option>
                  ))}
                </select>
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                >
                  <option value="">All States</option>
                  {US_STATES.map(state => (
                    <option key={state.code} value={state.code}>{state.name}</option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <Input
                  type="text"
                  placeholder="Enter city name"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* ZIP Code */}
              <div>
                <label className="block text-sm font-medium mb-1">ZIP Code</label>
                <Input
                  type="text"
                  placeholder="Enter ZIP code"
                  value={filters.zip}
                  onChange={(e) => handleFilterChange('zip', e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* NPI */}
              <div>
                <label className="block text-sm font-medium mb-1">NPI</label>
                <Input
                  type="text"
                  placeholder="Enter NPI number"
                  value={filters.npi}
                  onChange={(e) => handleFilterChange('npi', e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* CPT Code */}
              <div>
                <label className="block text-sm font-medium mb-1">CPT Code</label>
                <select
                  value={filters.cptCode}
                  onChange={(e) => handleFilterChange('cptCode', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                >
                  <option value="">All CPT Codes</option>
                  {COMMON_CPT_CODES.map(cpt => (
                    <option key={cpt.code} value={cpt.code}>
                      {cpt.code} - {cpt.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Results Limit */}
              <div>
                <label className="block text-sm font-medium mb-1">Results Limit</label>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                >
                  <option value={10}>10 results</option>
                  <option value={25}>25 results</option>
                  <option value={50}>50 results</option>
                  <option value={100}>100 results</option>
                </select>
              </div>
            </div>

            {/* Second Row - Location Search */}
            <div className="border-t pt-4 mt-4">
              {/* <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-primary" />
                <h4 className="text-md font-medium">Location-Based Search</h4>
              </div> */}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Address */}
                {/* <div>
                  <label className="block text-sm font-medium mb-1">Address or Location</label>
                  <Input
                    type="text"
                    placeholder="Enter address, city, or ZIP"
                    value={locationFilters.address}
                    onChange={(e) => handleLocationChange('address', e.target.value)}
                    className="text-sm"
                  />
                </div> */}

                {/* Radius */}
                {/* <div>
                  <label className="block text-sm font-medium mb-1">Radius (miles)</label>
                  <select
                    value={locationFilters.radius}
                    onChange={(e) => handleLocationChange('radius', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value={5}>5 miles</option>
                    <option value={10}>10 miles</option>
                    <option value={25}>25 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                  </select>
                </div> */}

                {/* Action Buttons */}
                <div className="flex items-end gap-2">
                  <Button 
                    type="submit" 
                    disabled={loading} 
                    className="flex-1"
                    onClick={handleSubmit}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search Providers
                  </Button>
                  {/* <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleLocationSubmit}
                    disabled={loading}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Find Nearby
                  </Button> */}
                  <Button type="button" variant="outline" onClick={handleReset}>
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
